import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { X, Copy, Trash2, Plus } from "lucide-react";
import { api } from "../api";
import { toast } from "sonner";

// Gestione accessi staff di un evento (proprietario). Più token attivi contemporaneamente,
// ciascuno revocabile singolarmente. Nessuna scadenza automatica: valido fino alla revoca.
export function StaffAccessManager({ event, onClose }) {
  const { t } = useTranslation();
  const [tokens, setTokens] = useState([]);
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => api.get(`/events/${event.id}/staff-tokens`).then(({ data }) => setTokens(data)).catch(() => setTokens([]));
  useEffect(() => { load(); }, []); // eslint-disable-line

  const linkFor = (path) => `${window.location.origin}${path}`;
  const create = async () => {
    setBusy(true);
    try { await api.post(`/events/${event.id}/staff-tokens`, { label: label.trim() }); setLabel(""); load(); }
    catch { toast.error("Errore"); } finally { setBusy(false); }
  };
  const copy = async (path) => { try { await navigator.clipboard.writeText(linkFor(path)); toast.success(t("staff.copied")); } catch { toast.error("Errore"); } };
  const revoke = async (id) => { if (!window.confirm(t("staff.revoke") + "?")) return; try { await api.post(`/events/${event.id}/staff-tokens/${id}/revoke`); load(); } catch { toast.error("Errore"); } };

  return (
    <div className="fixed inset-0 z-[9999] flex justify-end" data-testid="staff-manager">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white w-full sm:max-w-md h-full flex flex-col shadow-xl" role="dialog" aria-modal="true">
        <div className="sticky top-0 flex items-center justify-between px-4 py-3 border-b border-slate-100 bg-white">
          <div className="min-w-0"><h3 className="font-bold text-lg truncate">{t("staff.manage_title")}</h3><p className="text-xs text-slate-500 truncate">{event.title}</p></div>
          <button onClick={onClose} className="w-11 h-11 -mr-1 flex items-center justify-center rounded-full hover:bg-slate-100" aria-label="Chiudi" data-testid="staff-manager-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          <div className="yp-card p-4 space-y-2">
            <label className="text-sm font-medium">{t("staff.create_access")}</label>
            <div className="flex gap-2">
              <input className="yp-input flex-1" placeholder={t("staff.label_ph")} value={label} onChange={(e) => setLabel(e.target.value)} data-testid="staff-label-input" />
              <button className="yp-btn yp-btn-primary shrink-0 inline-flex items-center gap-1" onClick={create} disabled={busy} data-testid="staff-create-btn"><Plus className="w-4 h-4" />{t("staff.create_access")}</button>
            </div>
            <p className="text-xs text-slate-500">{t("staff.link_hint")}</p>
          </div>
          {tokens.length === 0 && <div className="text-slate-400 text-sm">{t("staff.no_tokens")}</div>}
          {tokens.map((tk) => (
            <div key={tk.id} className="yp-card p-4" data-testid="staff-token-row">
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-medium truncate">{tk.label || "—"}</div>
                  <div className="text-xs text-slate-500">{t("staff.created_on")} {new Date(tk.created_at).toLocaleString()}</div>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded-full shrink-0 ${tk.status === "active" ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-500"}`} data-testid="staff-token-status">{tk.status === "active" ? t("staff.active") : t("staff.revoked")}</span>
              </div>
              {tk.status === "active" && (
                <div className="flex gap-2 mt-3">
                  <button className="yp-btn yp-btn-outline flex-1 !py-1.5 text-xs inline-flex items-center justify-center gap-1" onClick={() => copy(tk.path)} data-testid="staff-copy-link"><Copy className="w-3.5 h-3.5" />{t("staff.copy_link")}</button>
                  <button className="yp-btn yp-btn-ghost !py-1.5 !px-3 text-xs text-red-600 inline-flex items-center gap-1" onClick={() => revoke(tk.id)} data-testid="staff-revoke-btn"><Trash2 className="w-3.5 h-3.5" />{t("staff.revoke")}</button>
                </div>)}
            </div>))}
        </div>
      </div>
    </div>);
}
